import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import { MoveToInbox } from '@mui/icons-material';
import {
  AdminFeatureRequest,
  FEATURE_REQUEST_CATEGORIES,
  FeatureRequestStatus,
  STATUS_LABELS,
} from '../../types/featureRequests';
import { colors } from '../../theme/theme';

const EDITABLE_STATUSES: FeatureRequestStatus[] = [
  'under_review',
  'planned',
  'in_progress',
  'not_planned',
  'custom_dev',
];

interface AdminRequestEditDialogProps {
  request: AdminFeatureRequest | null;
  onClose: () => void;
  onSave: (
    id: string,
    patch: Partial<{
      category: string;
      status: FeatureRequestStatus;
      visibility: 'public' | 'private';
      adminNotes: string;
      effortEstimate: number | null;
    }>,
  ) => Promise<void>;
}

const AdminRequestEditDialog: React.FC<AdminRequestEditDialogProps> = ({ request, onClose, onSave }) => {
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<FeatureRequestStatus>('under_review');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [adminNotes, setAdminNotes] = useState('');
  const [effortEstimate, setEffortEstimate] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (request) {
      setCategory(request.category);
      setStatus(request.status);
      setVisibility(request.visibility);
      setAdminNotes(request.adminNotes || '');
      setEffortEstimate(request.effortEstimate != null ? String(request.effortEstimate) : '');
    }
  }, [request]);

  if (!request) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(request._id, {
        category,
        status,
        visibility,
        adminNotes,
        effortEstimate: effortEstimate.trim() === '' ? null : Number(effortEstimate),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const moveToCustomDev = () => {
    setStatus('custom_dev');
    setVisibility('private');
  };

  return (
    <Dialog open={!!request} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{request.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
          {request.description}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.greyText, display: 'block', mb: 2 }}>
          Why: {request.whyNeeded}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${request.voteCount} votes`} />
          <Chip size="small" label={`Submitted by ${request.submittedByStudio?.studioName || 'a studio'}`} variant="outlined" />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {FEATURE_REQUEST_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as FeatureRequestStatus)}
              disabled={request.status === 'shipped'}
              helperText={request.status === 'shipped' ? 'Already shipped' : undefined}
            >
              {EDITABLE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              select
              fullWidth
              label="Visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as 'public' | 'private')}
            >
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="private">Private (custom development)</MenuItem>
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Effort estimate"
              value={effortEstimate}
              onChange={(e) => setEffortEstimate(e.target.value)}
            />
          </Box>

          <Button
            size="small"
            variant="outlined"
            startIcon={<MoveToInbox />}
            onClick={moveToCustomDev}
            disabled={status === 'custom_dev' && visibility === 'private'}
            sx={{ alignSelf: 'flex-start' }}
          >
            Move to custom development
          </Button>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Admin notes (internal only, never shown to studios)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            inputProps={{ maxLength: 2000 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminRequestEditDialog;
